import { Link, useLocation, useNavigate } from 'react-router-dom'
import { apiLogout } from '../api/auth/authApi'

type ProfilePillMenuProps = {
  /** Dropdown under the header pill (home). Panel sits on the right of account pages. */
  variant?: 'dropdown' | 'panel'
  open?: boolean
  onClose?: () => void
  onLogout?: () => void
}

const menuItems = [
  {
    id: 'saved-places',
    label: 'Saved Places',
    to: '/saved-places',
    icon: '/assets/images/pill-wishlist.svg',
    iconSize: 23,
  },
  {
    id: 'trips',
    label: 'My Trips',
    to: '/my-trips',
    icon: '/assets/images/pill-trips.svg',
    iconSize: 24,
  },
  {
    id: 'notifications',
    label: 'Notifications',
    to: '/notifications',
    icon: '/assets/images/pill-notifications.svg',
    iconSize: 24,
  },
  {
    id: 'profile',
    label: 'Profile',
    to: '/profile',
    icon: '/assets/images/pill-profile.svg',
    iconSize: 24,
  },
] as const

/**
 * Account menu — dropdown on home, right-side panel on profile modal pages (Figma 2027:38023).
 */
export default function ProfilePillMenu({
  variant = 'dropdown',
  open = true,
  onClose,
  onLogout,
}: ProfilePillMenuProps) {
  const location = useLocation()
  const navigate = useNavigate()

  if (variant === 'dropdown' && !open) return null

  const handleLogout = async () => {
    if (onLogout) {
      onLogout()
    } else {
      await apiLogout()
      navigate('/')
    }
    onClose?.()
  }

  const shellClass =
    variant === 'panel'
      ? 'flex w-[251px] flex-col gap-[11px] rounded-[12px] bg-white px-[7px] py-[13px] shadow-[0_8px_28px_rgba(0,0,0,0.12)]'
      : 'absolute right-0 top-[calc(100%+10px)] z-[60] flex w-[251px] flex-col gap-[11px] rounded-[12px] bg-white px-[7px] py-[13px] shadow-[0_8px_28px_rgba(0,0,0,0.12)]'

  return (
    <div role="menu" aria-label="Account menu" className={shellClass}>
      {menuItems.map((item) => {
        const isActive = location.pathname === item.to

        return (
          <Link
            key={item.id}
            to={item.to}
            role="menuitem"
            onClick={() => onClose?.()}
            className={`flex h-[40px] w-[237px] items-center gap-[6px] rounded-[12px] px-[14px] py-[3px] transition-colors hover:bg-[#F7F7F7] ${
              isActive ? 'bg-[#FFE8E8]' : 'bg-white'
            }`}
          >
            <span
              className="relative block shrink-0 overflow-hidden"
              style={{ width: item.iconSize, height: item.iconSize }}
            >
              <img
                alt=""
                aria-hidden
                src={item.icon}
                className="absolute inset-0 block size-full max-w-none"
              />
            </span>
            <span className="whitespace-nowrap font-host text-[16px] font-semibold leading-[1.69] tracking-[-0.48px] text-[#8C8C8C]">
              {item.label}
            </span>
          </Link>
        )
      })}

      <button
        type="button"
        role="menuitem"
        onClick={handleLogout}
        className="flex h-[40px] w-[237px] items-center gap-[6px] rounded-[12px] bg-white px-[14px] py-[3px] text-left transition-colors hover:bg-[#F7F7F7]"
      >
        <span className="relative block size-[29px] shrink-0 overflow-hidden">
          <img
            alt=""
            aria-hidden
            src="/assets/images/pill-logout.svg"
            className="absolute inset-0 block size-full max-w-none"
          />
        </span>
        <span className="whitespace-nowrap font-host text-[16px] font-semibold leading-[1.69] tracking-[-0.48px] text-[#8C8C8C]">
          Log Out
        </span>
      </button>
    </div>
  )
}
